/**
 * warehouse-setup-guides.tsx — per-type setup copy and external doc links for the
 * warehouse add-account configure step. Snowflake/Redshift use generated SQL in
 * the form; BigQuery/Databricks link to RudderStack destination guides with
 * read-only permission guidance.
 */
import type * as React from 'react';

import { ExternalDocLink } from '../components/ExternalDocLink';
import type { WarehouseTypeKey } from '../components/warehouseAccount/warehouseTypes';

export const BIGQUERY_SETUP_DOC_URL =
  'https://www.rudderstack.com/docs/destinations/warehouse-destinations/bigquery/#setting-up-the-service-account-for-rudderstack';

export const DATABRICKS_SETUP_DOC_URL =
  'https://www.rudderstack.com/docs/destinations/warehouse-destinations/delta-lake/';

export const CLICKHOUSE_SETUP_DOC_URL = 'https://www.rudderstack.com/docs/sources/reverse-etl/clickhouse/';

/**
 * Highlight Callout body for the add-account page, or undefined when none.
 *
 * Kept to a couple of lines (ACT2-1031): what Lookout needs to read, and the one extra grant
 * activation needs. The detail lives in the linked guides and in each form's generated SQL.
 */
export function warehouseSetupCallout(
  typeKey: WarehouseTypeKey,
  options: { snowflakeOwnKey?: boolean; snowflakeExistingRole?: boolean } = {},
): React.ReactNode | undefined {
  switch (typeKey) {
    case 'SNOWFLAKE':
      // The customer's own key (ACT2-1031): no generated key, no script to run.
      if (options.snowflakeOwnKey) {
        return (
          <>
            Lookout runs read-only queries: the service user's role needs read access to the database. To activate
            audiences, it also needs write access on <code>_RUDDERSTACK</code>.
          </>
        );
      }
      // An existing role keeps its own data grants; the script adds only database and warehouse USAGE.
      if (options.snowflakeExistingRole) {
        return (
          <>
            Lookout runs read-only queries with the role you pick: it reads whatever that role can read. To activate
            audiences, copy the optional grants for <code>_RUDDERSTACK</code> below the script, uncomment the lines your
            role lacks, and run them.
          </>
        );
      }
      // Still says database-wide out loud (ACT2-735): the script grants it, and a least-privilege
      // reviewer should learn that here rather than from reading the SQL.
      return (
        <>
          Lookout runs read-only queries: the script below grants read access across the <strong>whole database</strong>
          . To activate audiences, it also grants write access on <code>_RUDDERSTACK</code>.
        </>
      );
    case 'BQ':
      return (
        <>
          Lookout runs read-only queries: grant the service account <strong>BigQuery Data Viewer</strong> and{' '}
          <strong>Job User</strong>. To activate audiences, create a <code>rudderstack_</code> dataset in the same
          project if it does not exist, and grant <strong>BigQuery Data Editor</strong> on it.{' '}
          <ExternalDocLink href={BIGQUERY_SETUP_DOC_URL}>BigQuery destination setup guide</ExternalDocLink>
        </>
      );
    case 'DATABRICKS':
      return (
        <>
          Lookout runs read-only queries with a <strong>personal access token</strong> or an{' '}
          <strong>OAuth M2M service principal</strong>; the storage-bucket or staging permissions are not needed. To
          activate audiences, create <code>&lt;catalog&gt;._rudderstack</code> if it does not exist, and grant the
          principal <code>USE SCHEMA, CREATE TABLE, MODIFY, SELECT</code> on it.{' '}
          <ExternalDocLink href={DATABRICKS_SETUP_DOC_URL}>
            Databricks Delta Lake destination setup guide
          </ExternalDocLink>
        </>
      );
    case 'RS':
      return (
        <>
          Lookout runs read-only queries: the setup SQL below grants read access across the{' '}
          <strong>whole database</strong>. To activate audiences, it also creates <code>_rudderstack</code> and grants
          write access on it.
        </>
      );
    case 'POSTGRES':
      // Postgres has no generated script (the form takes an existing user), so the activation
      // grants have to arrive as prose.
      return (
        <>
          Lookout runs read-only queries: <code>USAGE</code> on your schemas and <code>SELECT</code> on their tables is
          enough. To activate audiences, create the <code>_rudderstack</code> schema, connected to the database Lookout
          queries, and grant this user <code>USAGE, CREATE</code> on it.
        </>
      );
    case 'CLICKHOUSE':
      return undefined;
    default: {
      const _exhaustive: never = typeKey;
      return _exhaustive;
    }
  }
}
